import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { SubSink } from "subsink";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";

@Component({
  selector: "app-overread-feedback",
  templateUrl: "./overread-feedback.component.html",
  styleUrls: ["./overread-feedback.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverreadFeedbackComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectConfigForm: FormGroup;
  @Input() projectAttributes: any;

  formGroup: FormGroup;
  overreadFeedbackEnabled: Switch;
  requireOverreadFeedback: Switch;
  overreadFeedbackChallengeEnabled: Switch;
  overread2FeedbackEnabled: Switch;
  requireOverread2Feedback: Switch;
  overread2FeedbackChallengeEnabled: Switch;
  private sink = new SubSink();

  constructor(
    private readonly formService: FormService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.projectConfigForm.addControl("overreadFeedbackFormGroup", this.formGroup);

    this.sink.add(this.formGroup.get("overreadFeedbackEnabled").valueChanges
      .subscribe(() => this.syncDependentControls("overreadFeedbackEnabled", "requireOverreadFeedback", "overreadFeedbackChallengeEnabled")));
    this.sink.add(this.formGroup.get("overread2FeedbackEnabled").valueChanges
      .subscribe(() => this.syncDependentControls("overread2FeedbackEnabled", "requireOverread2Feedback", "overread2FeedbackChallengeEnabled")));

    if (this.projectAttributes) {
      this.loadOverreadFeedbackSettings();
    }
  }

  ngOnChanges(): void {
    if (this.projectAttributes && this.formGroup) {
      this.loadOverreadFeedbackSettings();
    }
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  saveTo(projectAttributes: any): void {
    projectAttributes.OverreadFeedbackEnabled.attributeValue = this.getNumberValue("overreadFeedbackEnabled");
    projectAttributes.OverreadFeedbackAcknowledgmentRequired.attributeValue = this.getNumberValue("requireOverreadFeedback");
    projectAttributes.OverreadFeedbackChallengeEnabled.attributeValue = this.getNumberValue("overreadFeedbackChallengeEnabled");
    projectAttributes.Overread2FeedbackEnabled.attributeValue = this.getNumberValue("overread2FeedbackEnabled");
    projectAttributes.Overread2FeedbackAcknowledgmentRequired.attributeValue = this.getNumberValue("requireOverread2Feedback");
    projectAttributes.Overread2FeedbackChallengeEnabled.attributeValue = this.getNumberValue("overread2FeedbackChallengeEnabled");
  }

  private createForm(): void {
    this.overreadFeedbackEnabled = new Switch({
      key: "overreadFeedbackEnabled",
      label: "Overread I Feedback",
    });

    this.requireOverreadFeedback = new Switch({
      key: "requireOverreadFeedback",
      label: "Require Acknowledgment Before Data Entry",
      disabled: true,
    });
    this.overreadFeedbackChallengeEnabled = new Switch({
      key: "overreadFeedbackChallengeEnabled",
      label: "Enable Overread I Feedback Rebuttals",
      disabled: true,
    });
    this.overread2FeedbackEnabled = new Switch({
      key: "overread2FeedbackEnabled",
      label: "Overread II Feedback",
    });

    this.requireOverread2Feedback = new Switch({
      key: "requireOverread2Feedback",
      label: "Require Acknowledgment Before Data Entry",
      disabled: true,
    });
    this.overread2FeedbackChallengeEnabled = new Switch({
      key: "overread2FeedbackChallengeEnabled",
      label: "Enable Overread II Feedback Rebuttal",
      disabled: true,
    });

    this.formGroup = this.formService.createFormGroup([
      this.overreadFeedbackEnabled,
      this.requireOverreadFeedback,
      this.overreadFeedbackChallengeEnabled,
      this.overread2FeedbackEnabled,
      this.requireOverread2Feedback,
      this.overread2FeedbackChallengeEnabled,
    ]);
  }

  private loadOverreadFeedbackSettings(): void {
    // Key order matters: patching an "enabled" switch triggers syncDependentControls, which resets its
    // dependent switches, so the saved dependent values must be patched after it.
    this.formGroup.patchValue({
      overreadFeedbackEnabled: Number(this.projectAttributes.OverreadFeedbackEnabled?.attributeValue),
      requireOverreadFeedback: Number(this.projectAttributes.OverreadFeedbackAcknowledgmentRequired?.attributeValue),
      overreadFeedbackChallengeEnabled: Number(this.projectAttributes.OverreadFeedbackChallengeEnabled?.attributeValue),
      overread2FeedbackEnabled: Number(this.projectAttributes.Overread2FeedbackEnabled?.attributeValue),
      requireOverread2Feedback: Number(this.projectAttributes.Overread2FeedbackAcknowledgmentRequired?.attributeValue),
      overread2FeedbackChallengeEnabled: Number(this.projectAttributes.Overread2FeedbackChallengeEnabled?.attributeValue),
    });
    this.changeDetector.markForCheck();
  }

  private getNumberValue(key: string): number {
    return Number(this.formGroup.get(key).value);
  }

  private syncDependentControls(enabledKey: string, requireKey: string, challengeKey: string): void {
    const requireControl = this.formGroup.get(requireKey);
    const challengeControl = this.formGroup.get(challengeKey);

    if (this.getNumberValue(enabledKey) === 1) {
      requireControl.setValue(1);
      requireControl.enable();
      challengeControl.enable();
    } else {
      requireControl.setValue(0);
      requireControl.disable();
      challengeControl.setValue(0);
      challengeControl.disable();
    }
    this.changeDetector.markForCheck();
  }
}
